import { useEffect } from 'react';
import { supabase } from '../services/supabaseClient';

/**
 * Custom hook for user presence tracking
 * - Heartbeat: Updates last_active every 30 seconds
 * - Offline check: Runs every 60 seconds (admin only)
 * - Logout handler: Sets user offline
 */
export function usePresence(currentUser) {
  
  // Heartbeat: Update last_active every 30 seconds
  useEffect(() => {
    if (!currentUser || !supabase) return;
    
    const updateActivity = async () => {
      try {
        await supabase
          .from('users')
          .update({ last_active: new Date().toISOString() })
          .eq('id', currentUser.id);
      } catch (err) {
        console.error('⚠️ Heartbeat failed:', err);
        // Silent failure - don't break the app
      }
    };
    
    // Update immediately
    updateActivity();
    
    // Then every 30 seconds
    const interval = setInterval(updateActivity, 30000);
    
    return () => clearInterval(interval);
  }, [currentUser?.id]);

  // Run offline check every minute (admin only)
  useEffect(() => {
    if (!currentUser || currentUser.role !== 'admin' || !supabase) return;
    
    const checkOffline = async () => {
      try {
        await supabase.rpc('update_offline_users');
      } catch (err) {
        console.error('⚠️ Offline check failed:', err);
      }
    };
    
    const interval = setInterval(checkOffline, 60000);
    
    return () => clearInterval(interval);
  }, [currentUser?.id, currentUser?.role]);

  // Logout handler - set user offline
  useEffect(() => {
    return () => {
      // This runs when component unmounts (logout)
      if (currentUser && supabase) {
        supabase
          .from('users')
          .update({ 
            is_online: false,
            last_active: new Date().toISOString(),
          })
          .eq('id', currentUser.id)
          .catch(err => console.error('⚠️ Failed to set offline:', err));
      }
    };
  }, [currentUser?.id]);
}
